import { AdTypeOption, AdTypeOptions } from '@/_shared/types'
import { Stack, Text } from '@/components/custom-components'
import { FeatureTabs } from '@/components/custom-components/FeatureTabs'
import { Loader } from '@/components/custom-components/Loader'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { EmailFeedAdCard, LandingAdCard } from '@/components/templates/LandingAdCard'
import { trpc } from '@/utils/trpc.ts'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { EmailFeedContent } from '../../../../src/server/services/emails/definitions'
import { AirTableEmailRecord } from '../../../../src/shared'
const LIMIT = 10

type SearchParams = {
  sideBarOpen?: boolean
}

export const Route = createFileRoute('/feeds/saved/emails')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const sideBarOpen = search?.sideBarOpen as boolean
    return { sideBarOpen }
  },
})

function All() {
  const navigate = useNavigate()

  const { ref: scrollRef, inView } = useInView({
    threshold: 0,
    trackVisibility: true,
    delay: 100,
  })
  const cursor = 0

  const {
    data: savedEmailAds,
    isLoading: loadingEmailAds,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = trpc.getAllSavedEmails.useInfiniteQuery(
    { limit: LIMIT },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      initialCursor: cursor,
    },
  )

  const allData = useMemo(() => {
    if (!savedEmailAds) return []
    const records: EmailFeedContent[] = []
    for (const page of savedEmailAds.pages) {
      records.push(...page.emails)
    }
    return records.filter((item, index, self) => index === self.findIndex((obj) => obj.id === item.id))
  }, [savedEmailAds])

  useEffect(() => {
    if (!inView) return

    if (
      hasNextPage &&
      !isFetchingNextPage &&
      allData.length &&
      (savedEmailAds?.pages?.at(-1)?.emails?.length ?? 0) >= LIMIT
    ) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, allData.length, savedEmailAds])

  const noData = allData.length === 0

  // here are the template stuff delete when no longer needed
  const {
    data: savedEmailTemplates,
    hasNextPage: hasNextPageEmailTemplates,
    isLoading: loadingEmailTemplates,
    fetchNextPage: fetchNextPageEmailTemplates,
    isFetchingNextPage: isFetchingNextPageEmailTemplates,
  } = trpc.getAllSavedEmailTemplatesPaginated.useInfiniteQuery(
    { limit: LIMIT },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnWindowFocus: false,
      initialCursor: cursor,
    },
  )

  const allTemplatesData = useMemo(() => {
    if (!savedEmailTemplates) return []
    const records: AirTableEmailRecord[] = []
    for (const page of savedEmailTemplates.pages) {
      records.push(...page.results)
    }
    return records.filter(
      (item, index, self) => index === self.findIndex((obj) => obj['Email ID'] === item['Email ID']),
    )
  }, [savedEmailTemplates])

  useEffect(() => {
    if (!inView) return

    if (
      hasNextPageEmailTemplates &&
      !isFetchingNextPageEmailTemplates &&
      allTemplatesData.length &&
      (savedEmailTemplates?.pages?.at(-1)?.results?.length ?? 0) >= LIMIT
    ) {
      fetchNextPageEmailTemplates()
    }
  }, [
    inView,
    hasNextPageEmailTemplates,
    isFetchingNextPageEmailTemplates,
    fetchNextPageEmailTemplates,
    allTemplatesData.length,
    savedEmailTemplates,
  ])
  const noTemplateData = allTemplatesData.length === 0
  // here are the template stuff delete when no longer needed

  if (loadingEmailAds || loadingEmailTemplates) {
    return (
      <div className="flex justify-center items-center w-full h-screen m-auto">
        <Loader />
      </div>
    )
  }

  return (
    <Stack className="gap-3 lg:gap-8">
      <Stack className="gap-3 lg:gap-8">
        <div className="flex justify-between gap-2">
          <div>
            <Text weight="semibold" size="xxl">
              All Saved Email Inspiration
            </Text>
          </div>
          <div className="flex items-center gap-2 justify-end flex-wrap">
            <FeatureTabs
              tabItems={[
                { name: 'Ads', link: '/feeds/saved/ads' },
                { name: 'Landers', link: '/feeds/saved/ad-landers' },
                { name: 'Emails', link: '/feeds/saved/emails' },
              ]}
            />
            <Select
              value={AdTypeOptions.ALL_SAVED}
              onValueChange={(value: AdTypeOption) => {
                if (value === AdTypeOptions.ONLY_TEMPLATES) {
                  navigate({
                    to: `/feeds/saved/email-templates`,
                  })
                }
              }}
            >
              <SelectTrigger className="w-33 h-8 max-h-8">
                <SelectValue placeholder="Ad Type" className="rounded-md" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Object.values(AdTypeOptions).map((item) => (
                    <SelectItem key={item} value={item} className="py-1.5">
                      {item}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Stack>

      <div>
        {noData && noTemplateData ? (
          <div className="h-[80vh] flex justify-center items-center">
            <p className="text-center">You do not have any ad email inspiration saved</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {allData.map((card) => (
              <EmailFeedAdCard key={card.id} adData={card} />
            ))}
            {allTemplatesData.map((card) => (
              <LandingAdCard
                key={card['Email ID']}
                adData={{
                  Screenshot: card['Email Screenshot'],
                  ID: `${card['Email ID']}`,
                  created: card.Created,
                  category: card.Category,
                  isSaved: card.isSaved,
                  imageUrl: card.imageUrl,
                }}
                showTemplateButton
                type="email"
              />
            ))}
          </div>
        )}
      </div>

      <div ref={scrollRef} className="flex justify-center w-full min-h-14 h-14">
        {isFetchingNextPage && <Loader />}
      </div>
    </Stack>
  )
}
